import {
  Card,
  CrudMode,
  DataDisplay,
  formatSchoolYear,
  GeneralValidationErrors,
  Grid,
  pn,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { Input, Select } from "components";
import { StudentGpa, StudentGpaEditResource, StudentGpaViewResource, StudentGrade } from "generated/openApi";

import produce from "immer";
import "moment-timezone";

export const StudentGpaPageBody = (props: {
  crudMode: CrudMode;
  gpa: StudentGpa;
  setGpa: (value: StudentGpa) => void;
  viewResource: StudentGpaViewResource;
  editResource: StudentGpaEditResource;
  validationErrors: ValidationErrors;
}) => {
  const properties = props.viewResource.properties || [];
  const { breakpointsRef, smallFieldBreakpoints, fullFieldBreakpoints } = useElementBreakpoints();
  const generationValidationErrors = props.validationErrors[pn<StudentGrade>("generalValidationErrors")];

  const termCount = props.viewResource.termTypes?.find((x) => x.id === props.gpa.termTypeId)?.termCount || 0;

  const yearField = () => {
    const propertyName = pn<StudentGpa>("year");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{formatSchoolYear(props.gpa.year)}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schoolYears?.find((x) => x.id === props.gpa.year)}
        options={props.editResource.schoolYears}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGpa(
            produce(props.gpa, (draft) => {
              draft.year = value?.id;
            })
          )
        }
      />
    );
  };

  const schoolField = () => {
    const propertyName = pn<StudentGpa>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.gpa.school?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.schools?.find((x) => x.id === props.gpa.schoolId)}
        options={props.editResource.schools}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGpa(
            produce(props.gpa, (draft) => {
              draft.schoolId = value?.id;
            })
          )
        }
      />
    );
  };

  const termTypeField = () => {
    const propertyName = pn<StudentGpa>("termTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.gpa.termType?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.viewResource.termTypes?.find((x) => x.id === props.gpa.termTypeId)}
        options={props.viewResource.termTypes}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGpa(
            produce(props.gpa, (draft) => {
              draft.termTypeId = value?.id;
              draft.termNumber = undefined;
            })
          )
        }
      />
    );
  };

  const termNumberField = () => {
    const propertyName = pn<StudentGpa>("termNumber");
    const property = properties.find((x) => x.propertyName === propertyName);
    const termNumbers = Array.from({ length: termCount }, (_, i) => i + 1);

    return termCount > 0 ? (
      props.crudMode === "view" ? (
        <DataDisplay label={property?.displayName}>{props.gpa.termNumber}</DataDisplay>
      ) : (
        <Select
          modelProperty={property}
          required
          errors={props.validationErrors[propertyName]}
          value={termNumbers.find((x) => x === props.gpa.termNumber)}
          options={termNumbers}
          getOptionLabel={(option) => option.toString()}
          onChange={(e, value) =>
            props.setGpa(
              produce(props.gpa, (draft) => {
                draft.termNumber = value;
              })
            )
          }
        />
      )
    ) : null;
  };

  const gpaField = () => {
    const propertyName = pn<StudentGpa>("gpa");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.gpa.gpa}</DataDisplay>
    ) : (
      <Input
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.gpa.gpa}
        onChange={(e) =>
          props.setGpa(
            produce(props.gpa, (draft) => {
              draft.gpa = parseFloat(e.target.value);
            })
          )
        }
      />
    );
  };

  const reasonMissingField = () => {
    const propertyName = pn<StudentGpa>("reasonMissingId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.gpa.reasonMissing?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.reasonMissings?.find((x) => x.id === props.gpa.reasonMissingId)}
        options={props.editResource.reasonMissings}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGpa(
            produce(props.gpa, (draft) => {
              draft.reasonMissingId = value?.id;
            })
          )
        }
      />
    );
  };

  const calculationField = () => {
    const propertyName = pn<StudentGpa>("calculationId");
    const property = properties.find((x) => x.propertyName === propertyName);

    return props.crudMode === "view" ? (
      <DataDisplay label={property?.displayName}>{props.gpa.calculation?.name}</DataDisplay>
    ) : (
      <Select
        modelProperty={property}
        errors={props.validationErrors[propertyName]}
        value={props.editResource.calculations?.find((x) => x.id === props.gpa.calculationId)}
        options={props.editResource.calculations}
        getOptionLabel={(option) => option.name}
        onChange={(e, value) =>
          props.setGpa(
            produce(props.gpa, (draft) => {
              draft.calculationId = value?.id;
            })
          )
        }
      />
    );
  };

  return (
    <Grid ref={breakpointsRef} container spacing="sm">
      {generationValidationErrors && (
        <Grid item xs={fullFieldBreakpoints}>
          <GeneralValidationErrors errors={generationValidationErrors} />
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {yearField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {schoolField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {termTypeField()}
      </Grid>

      {termCount > 0 && (
        <Grid item xs={smallFieldBreakpoints}>
          {termNumberField()}
        </Grid>
      )}

      <Grid item xs={smallFieldBreakpoints}>
        {gpaField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {calculationField()}
      </Grid>

      <Grid item xs={smallFieldBreakpoints}>
        {reasonMissingField()}
      </Grid>
    </Grid>
  );
};
